import { useMemo } from 'react'
import DashboardLayout from '../../layouts/LayoutContainers/DashboardLayout'
import PremiumLocked from '../../components/Items/Lock'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSort } from '@fortawesome/free-solid-svg-icons'
import 'react-dropdown/style.css'
import 'animate.css'

import useRanking, { SortBy } from './useRanking'
import RankingSkeleton from './RankingSkeleton'
import RankingFilters from './RankingFilters'
import Pagination from './Pagination'

const Ranking = () => {
  const {
    sortType,
    setSortType,
    handleGetDisplayName,
    currentUserPosition,
    handleGetDisplayEye,
    hasSubscription,
    maxLeaderboardAnimationDelay,
    usersData,
    currentUserData,
    nrUsersDisplayed,
    isLoading,
    handlePeriodValue,
    handleSelectedCourse,
    courseValue,
    filterPeriodValue,
    faculties,
    handleFacultyValue,
    faculty,
    handlePageChange,
    currentPage,
    handleNextPage,
    handlePreviousPage,
    MIN_PAGE,
    MAX_PAGE,
    pages,
  } = useRanking()

  const tableHeader = useMemo(() => {
    return (
      <tr className="text-over-secondary-background">
        <th key="Nr" className="mb-5 opacity-80 min-w-5 max-w-10 flex-grow">
          Nr.
        </th>
        <th key="Name" className="opacity-80 min-w-10 max-w-20 flex-grow">
          Nume
        </th>
        <th
          key="SolvedGrids"
          className={`mb-5 min-w-10 max-w-20 flex-grow opacity-80 hover:cursor-pointer hover:opacity-100 hover:text-primary hover:transition ease-in-out delay-200
          ${sortType === 0 && 'text-primary opacity-100'}`}
          onClick={() => {
            setSortType(SortBy.SOLVED_GRIDS)
          }}
        >
          Grile rezolvate{' '}
          <FontAwesomeIcon
            icon={faSort}
            onClick={() => {
              setSortType(SortBy.SOLVED_GRIDS)
            }}
          />
        </th>
        <th
          key="CorrectlySolvedGrids"
          className={`mb-5 min-w-10 max-w-20 flex-grow opacity-80 hover:cursor-pointer hover:opacity-100 hover:text-primary hover:transition ease-in-out delay-200
          ${sortType === 1 && 'text-primary opacity-100'}`}
          onClick={() => {
            setSortType(SortBy.CORRECTLY_SOLVED_GRIDS)
          }}
        >
          Grile rezolvate corect{' '}
          <FontAwesomeIcon
            icon={faSort}
            onClick={() => {
              setSortType(SortBy.CORRECTLY_SOLVED_GRIDS)
            }}
          />
        </th>
        <th
          key="CorrectPercentage"
          className={`mb-5 min-w-10 max-w-20 flex-grow opacity-80 hover:cursor-pointer hover:opacity-100 hover:text-primary  hover:transition ease-in-out delay-200
          ${sortType === 2 && 'text-primary opacity-100'}`}
          onClick={() => {
            setSortType(SortBy.CORRECT_PERCENTAGE)
          }}
        >
          Procent corecte{' '}
          <FontAwesomeIcon
            icon={faSort}
            onClick={() => {
              setSortType(SortBy.CORRECT_PERCENTAGE)
            }}
          />
        </th>
        <th
          key="PercentageOfIndications"
          className="opacity-80 min-w-10 max-w-20 flex-grow"
        >
          Procent indicii
        </th>
      </tr>
    )
  }, [sortType, setSortType])

  const mappedUsersList = useMemo(() => {
    const startIndex = (currentPage - 1) * nrUsersDisplayed
    const endIndex = startIndex + nrUsersDisplayed
    return usersData?.[sortType]
      ?.slice(startIndex, endIndex)
      .map((user, index) => {
        const isFirstPlace = startIndex + index === 0
        const isSecondPlace = startIndex + index === 1
        const isThirdPlace = startIndex + index === 2

        return (
          <tr
            key={`leaderboard-row-${index}`}
            className={`hover:bg-ternary hover:transition ease-in-out delay-200 hover:text-white ${
              startIndex + index === currentUserPosition[sortType]
                ? 'bg-[#00D9B2FF] !text-over-secondary-background hover:!text-white'
                : ''
            } 
          ${isFirstPlace ? 'text-[#FFD700]' : ''} 
          ${isSecondPlace ? 'text-gray-400' : ''} 
          ${isThirdPlace ? 'text-orange-500' : ''} 
          ${
            !isFirstPlace && !isSecondPlace && !isThirdPlace
              ? 'text-over-secondary-background'
              : ''
          }
        `}
          >
            <th className="min-w-5 max-w-10 flex-grow">
              {startIndex + index + 1}
            </th>
            <th className="min-w-10 max-w-20 flex-grow">
              {handleGetDisplayName(user)}
              {startIndex + index === currentUserPosition[sortType] &&
                handleGetDisplayEye(user)}
            </th>
            <th className="min-w-10 max-w-20 flex-grow">
              {user.grile_rezolvate}
            </th>
            <th className="min-w-10 max-w-20 flex-grow">
              {user.grile_rezolvate_corect}
            </th>
            <th className="min-w-10 max-w-20 flex-grow">
              {Math.floor(
                (user.grile_rezolvate_corect / user.grile_rezolvate) * 100,
              )}
              %
            </th>
            <th className="min-w-20 max-w-20 flex-grow">
              {Math.floor((user.indicii / user.grile_rezolvate) * 100)}%
            </th>
          </tr>
        )
      })
  }, [
    usersData,
    sortType,
    currentUserPosition,
    currentPage,
    nrUsersDisplayed,
    handleGetDisplayEye,
    handleGetDisplayName,
  ])

  const hasNoSubscriptionRow = useMemo(() => {
    return (
      <tr
        key="has-no-subscription-row"
        style={{ animationDelay: `${maxLeaderboardAnimationDelay}s` }}
        className="leaderboard-row  leaderboard-row-highlighted"
      >
        <th key="position" className="leaderboard-position">
          <PremiumLocked descriptionText="Disponibil doar cu abonament" />
        </th>
        <th
          key="name"
          className="leaderboard-name text-over-primary-background"
        >
          {handleGetDisplayName(currentUserData)}
        </th>
        <th key="info-1" className="leaderboard-info-1">
          <PremiumLocked descriptionText="Disponibil doar cu abonament" />
        </th>
        <th key="info-2" className="leaderboard-info-2">
          <PremiumLocked descriptionText="Disponibil doar cu abonament" />
        </th>
        <th key="info-3" className="leaderboard-info-3">
          <PremiumLocked descriptionText="Disponibil doar cu abonament" />
        </th>
        <th key="info-4" className="leaderboard-info-4">
          <PremiumLocked descriptionText="Disponibil doar cu abonament" />
        </th>
      </tr>
    )
  }, [maxLeaderboardAnimationDelay, currentUserData, handleGetDisplayName])

  const hasSubscriptionRow = useMemo(() => {
    return (
      <tr className="bg-primary !text-white" key="has-subscription-row">
        <th>
          {currentUserData.grile_rezolvate > 20
            ? currentUserPosition[sortType]
            : '-'}
        </th>
        <th>
          {handleGetDisplayName(currentUserData)}
          {handleGetDisplayEye(currentUserData)}
        </th>
        <th>{currentUserData.grile_rezolvate}</th>
        <th>{currentUserData.grile_rezolvate_corect}</th>
        <th>
          {currentUserData.grile_rezolvate !== 0
            ? Math.floor(
                (currentUserData.grile_rezolvate_corect /
                  currentUserData.grile_rezolvate) *
                  100,
              )
            : '0'}
          %
        </th>
        <th>
          {currentUserData.grile_rezolvate !== 0
            ? Math.floor(
                (currentUserData.indicii / currentUserData.grile_rezolvate) *
                  100,
              )
            : '0'}
          %
        </th>
      </tr>
    )
  }, [
    currentUserData,
    currentUserPosition,
    sortType,
    handleGetDisplayEye,
    handleGetDisplayName,
  ])

  const table = useMemo(() => {
    return (
      <table className="w-full h-full !bg-secondary-background text-xs md:text-sm overflow-y-auto">
        <thead>{tableHeader}</thead>
        <tbody>
          {mappedUsersList}
          {!hasSubscription
            ? hasNoSubscriptionRow
            : currentUserPosition[sortType] > nrUsersDisplayed
              ? hasSubscriptionRow
              : null}
        </tbody>
      </table>
    )
  }, [
    tableHeader,
    mappedUsersList,
    hasSubscription,
    hasNoSubscriptionRow,
    currentUserPosition,
    sortType,
    nrUsersDisplayed,
    hasSubscriptionRow,
  ])
  return (
    <DashboardLayout>
      <div className="flex justify-center gap-3 flex-col items-center h-full">
        {/* <div className="w-full"> */}
        <RankingFilters
          hasSubscription={hasSubscription}
          faculty={faculty}
          handleFacultyChange={handleFacultyValue}
          faculties={faculties}
          filterPeriodValue={filterPeriodValue}
          handlePeriodValue={handlePeriodValue}
          courseValue={courseValue}
          handleSelectedCourse={handleSelectedCourse}
        />
        {/* </div> */}
        <div className="relative bg-secondary-background h-[75svh] max-h-[800px] min-h-[450px] mb-[20px] border min-w-[60vw] p-2 pt-1 rounded-[12px] !pb-[50px] border-brd-primary shadow-lg">
          {isLoading ? <RankingSkeleton /> : table}
          {!isLoading ? (
            <div className="flex justify-center items-center">
              <Pagination
                hasSubscription={hasSubscription}
                disableRightArrow={currentPage === MAX_PAGE}
                disableLeftArrow={currentPage === MIN_PAGE}
                pages={pages}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                handleNextPage={handleNextPage}
                handlePreviousPage={handlePreviousPage}
              />
            </div>
          ) : null}
        </div>
      </div>
    </DashboardLayout>
  )
}

export default Ranking
